// =============================================================================
// Plugin Panel Page - Appthen 插件主面板
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Play,
  Square,
  Plus,
  Terminal,
  ChevronDown,
  ChevronRight,
  Circle,
  Activity,
  Cpu,
  HardDrive,
  LogOut
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/apiClient';
import LoadingSpinner from '../components/LoadingSpinner';
import CreateWorkspaceModal from '../components/CreateWorkspaceModal';
import CreateTerminalModal from '../components/CreateTerminalModal';
import '../styles/plugin.css';

const PluginPanelPage = () => {
  const { user, isAuthenticated, login, register, isLoading: authLoading } = useAuth();
  const { workspaces, isLoading, startWorkspace, stopWorkspace, getWorkspaceStats, loadWorkspaces } = useWorkspace();
  const [expandedWorkspaces, setExpandedWorkspaces] = useState(new Set());
  const [workspaceStats, setWorkspaceStats] = useState({});
  const [workspaceTerminals, setWorkspaceTerminals] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTerminalModal, setShowCreateTerminalModal] = useState(false);
  const [selectedWorkspaceForTerminal, setSelectedWorkspaceForTerminal] = useState(null);

  // 登录注册状态
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [authForm, setAuthForm] = useState({
    username: '',
    password: '',
    email: '',
    confirmPassword: ''
  });
  const [authError, setAuthError] = useState('');

  // PostMessage API - 与 Appthen 通信
  const sendMessageToParent = useCallback((type, data) => {
    const message = {
      type,
      data,
      source: 'ai-workspace-plugin',
      timestamp: new Date().toISOString()
    };

    console.log('🔌 Sending message to parent:', message);

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  }, []);

  // 处理来自 Appthen 的消息
  const handleParentMessage = useCallback((event) => {
    // 安全检查：只处理来自预期源的消息
    if (!event.data || typeof event.data !== 'object') return;
    if (!event.data.type || event.data.source === 'ai-workspace-plugin') return;

    console.log('🔌 Received message from parent:', event.data);

    const { type, data } = event.data;

    switch (type) {
      case 'PLUGIN_READY_CHECK':
        // Appthen 检查插件是否准备就绪
        sendMessageToParent('PLUGIN_READY', {
          ready: isAuthenticated && !authLoading,
          user: user?.username,
          workspaceCount: workspaces?.length || 0
        });
        break;

      case 'REQUEST_WORKSPACE_LIST':
        // Appthen 请求工作空间列表
        sendMessageToParent('WORKSPACE_LIST', {
          workspaces: workspaces.map(ws => ({
            id: ws.id,
            name: ws.name,
            status: ws.status,
            template: ws.template,
            createdAt: ws.createdAt
          }))
        });
        break;

      case 'REQUEST_TERMINAL_LIST':
        // Appthen 请求指定工作空间的终端列表
        if (data?.workspaceId && workspaceTerminals[data.workspaceId]) {
          sendMessageToParent('TERMINAL_LIST', {
            workspaceId: data.workspaceId,
            terminals: workspaceTerminals[data.workspaceId]
          });
        }
        break;

      default:
        console.log('🔌 Unknown message type:', type);
    }
  }, [isAuthenticated, authLoading, user, workspaces, workspaceTerminals, sendMessageToParent]);

  // 设置 PostMessage 监听器
  useEffect(() => {
    window.addEventListener('message', handleParentMessage);

    // 插件加载完成后通知父窗口
    sendMessageToParent('PLUGIN_LOADED', {
      ready: isAuthenticated && !authLoading,
      user: user?.username
    });

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, [handleParentMessage, sendMessageToParent, isAuthenticated, authLoading, user]);




  // 获取单个工作空间统计信息（按需加载）
  const fetchWorkspaceStats = async (workspaceId) => {
    try {
      const stats = await getWorkspaceStats(workspaceId);
      setWorkspaceStats(prev => ({
        ...prev,
        [workspaceId]: stats
      }));
    } catch (error) {
      console.error(`Failed to fetch stats for workspace ${workspaceId}:`, error);
    }
  };

  // 获取工作空间的终端列表
  const fetchWorkspaceTerminals = async (workspaceId) => {
    try {
      const response = await apiClient.get(`/plugin/terminals?workspaceId=${workspaceId}`);
      if (response.data.success) {
        const terminals = response.data.data.terminals[workspaceId] || [];
        setWorkspaceTerminals(prev => ({
          ...prev,
          [workspaceId]: terminals
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch terminals for workspace ${workspaceId}:`, error);
    }
  };

  const toggleWorkspaceExpansion = (workspaceId) => {
    const newExpanded = new Set(expandedWorkspaces);
    if (newExpanded.has(workspaceId)) {
      newExpanded.delete(workspaceId);
    } else {
      newExpanded.add(workspaceId);
      // 展开时获取统计信息和终端列表
      if (!workspaceStats[workspaceId]) {
        fetchWorkspaceStats(workspaceId);
      }
      if (!workspaceTerminals[workspaceId]) {
        fetchWorkspaceTerminals(workspaceId);
      }
    }
    setExpandedWorkspaces(newExpanded);
  };

  const handleCreateTerminal = (workspaceId) => {
    setSelectedWorkspaceForTerminal(workspaceId);
    setShowCreateTerminalModal(true);
  };

  const handleCreateTerminalSuccess = (terminal) => {
    const workspaceId = selectedWorkspaceForTerminal;

    // 刷新终端列表
    setWorkspaceTerminals(prev => ({
      ...prev,
      [workspaceId]: undefined // 清除缓存，强制重新获取
    }));
    fetchWorkspaceTerminals(workspaceId);

    // 通知父窗口打开新终端
    window.parent.postMessage({
      type: 'OPEN_TERMINAL',
      terminalId: terminal.terminalId,
      workspaceId: workspaceId
    }, '*');
  };

  const handleCreateWorkspaceSuccess = async (workspace) => {
    // 刷新工作空间列表
    await loadWorkspaces();

    // 通知父窗口工作空间创建成功
    window.parent.postMessage({
      type: 'WORKSPACE_CREATED',
      workspace: workspace
    }, '*');
  };

  const handleOpenTerminal = (terminalId, workspaceId) => {
    // 通知父窗口打开终端
    sendMessageToParent('OPEN_TERMINAL', {
      terminalId: terminalId,
      workspaceId: workspaceId
    });
  };

  // 包装工作空间操作函数，添加PostMessage通知
  const handleStartWorkspace = async (workspaceId) => {
    try {
      await startWorkspace(workspaceId);
      sendMessageToParent('WORKSPACE_STATUS_CHANGED', {
        workspaceId,
        status: 'starting',
        action: 'start'
      });
    } catch (error) {
      sendMessageToParent('ERROR', {
        message: `启动工作空间失败: ${error.message}`,
        workspaceId
      });
    }
  };

  const handleStopWorkspace = async (workspaceId) => {
    try {
      await stopWorkspace(workspaceId);
      sendMessageToParent('WORKSPACE_STATUS_CHANGED', {
        workspaceId,
        status: 'stopping',
        action: 'stop'
      });
    } catch (error) {
      sendMessageToParent('ERROR', {
        message: `停止工作空间失败: ${error.message}`,
        workspaceId
      });
    }
  };

  // 处理认证表单提交
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');

    try {
      if (authMode === 'login') {
        await login({
          username: authForm.username,
          password: authForm.password
        });
      } else {
        if (authForm.password !== authForm.confirmPassword) {
          setAuthError('密码确认不匹配');
          return;
        }
        await register({
          username: authForm.username,
          password: authForm.password,
          email: authForm.email
        });
      }
      setShowAuth(false);
      setAuthForm({ username: '', password: '', email: '', confirmPassword: '' });
    } catch (error) {
      setAuthError(error.message || '认证失败');
    }
  };

  // 切换认证模式
  const toggleAuthMode = () => {
    setAuthMode(authMode === 'login' ? 'register' : 'login');
    setAuthError('');
    setAuthForm({ username: '', password: '', email: '', confirmPassword: '' });
  };



  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'text-green-500';
      case 'stopped': return 'text-gray-500';
      case 'starting': return 'text-yellow-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return <Circle className="w-2 h-2 fill-current" />;
      case 'stopped': return <Circle className="w-2 h-2" />;
      case 'starting': return <Activity className="w-3 h-3 animate-pulse" />;
      case 'error': return <Circle className="w-2 h-2 fill-current" />;
      default: return <Circle className="w-2 h-2" />;
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  // 如果未认证，显示登录界面
  if (!isAuthenticated) {
    return (
      <div className="w-full h-screen bg-background text-foreground flex flex-col">
        {/* 头部 */}
        <div className="h-12 border-b border-border flex items-center justify-between px-3">
          <h2 className="text-sm font-medium">AI 开发工作空间</h2>
        </div>

        {/* 登录/注册界面 */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold mb-2">
                {authMode === 'login' ? '登录' : '注册'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {authMode === 'login' ? '登录以管理您的工作空间' : '创建新账户'}
              </p>
            </div>

            <form onSubmit={handleAuthSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="用户名"
                  value={authForm.username}
                  onChange={(e) => setAuthForm({...authForm, username: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {authMode === 'register' && (
                <div>
                  <input
                    type="email"
                    placeholder="邮箱"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              )}

              <div>
                <input
                  type="password"
                  placeholder="密码"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {authMode === 'register' && (
                <div>
                  <input
                    type="password"
                    placeholder="确认密码"
                    value={authForm.confirmPassword}
                    onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              )}

              {authError && (
                <div className="text-xs text-red-500 text-center">
                  {authError}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-2 px-4 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {authMode === 'login' ? '登录' : '注册'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={toggleAuthMode}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {authMode === 'login' ? '没有账户？点击注册' : '已有账户？点击登录'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen bg-background text-foreground overflow-hidden">
      {/* 头部 */}
      <div className="h-12 border-b border-border flex items-center justify-between px-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium">工作空间</h2>
          <span className="text-xs text-muted-foreground">({user?.username})</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowCreateModal(true)}
            className="p-1 hover:bg-accent rounded text-xs flex items-center gap-1"
            title="创建新工作空间"
          >
            <Plus className="w-3 h-3" />
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('token');
              window.location.reload();
            }}
            className="p-1 hover:bg-accent rounded text-xs"
            title="登出"
          >
            <LogOut className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* 工作空间列表 */}
      <div className="flex-1 overflow-y-auto">
        {!workspaces || workspaces.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-xs">
            暂无工作空间
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {workspaces.map((workspace) => {
              const isExpanded = expandedWorkspaces.has(workspace.id);
              const stats = workspaceStats[workspace.id];

              return (
                <div key={workspace.id} className="border border-border rounded-lg">
                  {/* 工作空间头部 */}
                  <div className="p-2">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleWorkspaceExpansion(workspace.id)}
                        className="flex items-center gap-1.5 flex-1 text-left hover:bg-accent rounded px-1 py-0.5"
                      >
                        {isExpanded ?
                          <ChevronDown className="w-3 h-3 text-muted-foreground" /> :
                          <ChevronRight className="w-3 h-3 text-muted-foreground" />
                        }
                        <div className={`flex items-center gap-1.5 ${getStatusColor(workspace.status)}`}>
                          {getStatusIcon(workspace.status)}
                          <div className="flex flex-col">
                            <span className="text-xs font-medium truncate">
                              {workspace.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {workspace.templateId || 'nodejs'}
                            </span>
                          </div>
                        </div>
                      </button>

                      <div className="flex items-center gap-0.5">
                        {workspace.status === 'running' ? (
                          <button
                            onClick={() => handleStopWorkspace(workspace.id)}
                            className="p-1 hover:bg-accent rounded"
                            title="停止工作空间"
                          >
                            <Square className="w-3 h-3 text-red-500" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStartWorkspace(workspace.id)}
                            className="p-1 hover:bg-accent rounded"
                            title="启动工作空间"
                          >
                            <Play className="w-3 h-3 text-green-500" />
                          </button>
                        )}

                        <button
                          onClick={() => handleCreateTerminal(workspace.id)}
                          className="p-1 hover:bg-accent rounded disabled:opacity-50"
                          title="新建终端"
                          disabled={workspace.status !== 'running'}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 工作空间详细信息 */}
                  {isExpanded && (
                    <div className="border-t border-border p-2 bg-accent/20">
                      <div className="space-y-2">
                        {/* 基本信息 */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">模板:</span>
                            <span className="ml-1 font-medium">{workspace.templateId || 'nodejs'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">状态:</span>
                            <span className={`ml-1 font-medium ${getStatusColor(workspace.status)}`}>
                              {workspace.status}
                            </span>
                          </div>
                        </div>

                        {/* 资源使用情况 */}
                        {stats && (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">资源使用:</div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="flex items-center gap-1">
                                <Cpu className="w-3 h-3 text-blue-500" />
                                <span>CPU: {stats.cpu?.toFixed(1) || 0}%</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <HardDrive className="w-3 h-3 text-green-500" />
                                <span>内存: {stats.memory?.percent?.toFixed(1) || 0}%</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 创建时间 */}
                        <div className="text-xs">
                          <span className="text-muted-foreground">创建:</span>
                          <span className="ml-1">{new Date(workspace.createdAt).toLocaleDateString()}</span>
                        </div>

                        {/* 描述 */}
                        {workspace.description && (
                          <div className="text-xs text-muted-foreground bg-background/50 p-2 rounded">
                            {workspace.description}
                          </div>
                        )}

                        {/* 终端列表 */}
                        {workspaceTerminals[workspace.id] && workspaceTerminals[workspace.id].length > 0 && (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">终端列表:</div>
                            <div className="space-y-1">
                              {workspaceTerminals[workspace.id].map((terminal) => (
                                <div
                                  key={terminal.id}
                                  className="flex items-center justify-between p-1.5 bg-background/50 rounded text-xs"
                                >
                                  <div className="flex items-center gap-1.5">
                                    <Terminal className="w-3 h-3 text-green-500" />
                                    <span className="font-medium">{terminal.name || `终端 ${terminal.id.slice(0, 8)}`}</span>
                                    <span className="text-muted-foreground">({terminal.shell})</span>
                                  </div>
                                  <button
                                    onClick={() => handleOpenTerminal(terminal.id, workspace.id)}
                                    className="px-2 py-0.5 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                                  >
                                    打开
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* 操作按钮 */}
                        <button
                          onClick={() => handleCreateTerminal(workspace.id)}
                          className="w-full flex items-center justify-center gap-1.5 p-1.5 bg-primary text-primary-foreground rounded text-xs hover:bg-primary/90 disabled:opacity-50"
                          disabled={workspace.status !== 'running'}
                        >
                          <Terminal className="w-3 h-3" />
                          创建新终端
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 创建工作空间模态框 */}
      <CreateWorkspaceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateWorkspaceSuccess}
      />

      {/* 创建终端模态框 */}
      <CreateTerminalModal
        isOpen={showCreateTerminalModal}
        onClose={() => {
          setShowCreateTerminalModal(false);
          setSelectedWorkspaceForTerminal(null);
        }}
        onSuccess={handleCreateTerminalSuccess}
        workspaceId={selectedWorkspaceForTerminal}
      />
    </div>
  );
};

export default PluginPanelPage;
